/**
 * AI 对话消息处理共享常量与工具
 *
 * ★ 目的：统一 App.tsx（SSE 实时）与 ai-voice/page.tsx（历史加载）的消息解析逻辑，
 *   避免两处规则分叉导致"实时有卡片、刷新后卡片消失"等问题。
 *
 * ★ 新增查询卡片类型时，只需修改 QUERY_MSG_TYPES 一处。
 */

/** 会渲染查询结果卡片的 msg_type 列表（需与 query-results/index.tsx 的 switch 保持一致） */
export const QUERY_MSG_TYPES = [
  "train",
  "car",
  "food",
  "guide",
  "flight",
  "hotel",
  "route",
  "transfer",
] as const;

export type QueryMsgType = (typeof QUERY_MSG_TYPES)[number];

/** 是否为查询类消息（需要渲染卡片） */
export function isQueryMsgType(msg_type?: string): boolean {
  if (!msg_type) return false;
  return (QUERY_MSG_TYPES as readonly string[]).includes(msg_type);
}

/** 是否为"订单已创建"消息（action=book，数据在 __data） */
export function isBookingOrderMsg(d?: any): boolean {
  return d?.action === "book";
}

/** 是否为 AI 系统消息（navigation / tts_audio）：强制 role=ai，文本取 content */
export function isAiSystemMsg(m: any): boolean {
  return (
    m?.msg_type === "navigation" || m?.action === "navigate" ||
    m?.msg_type === "tts_audio" || m?.action === "tts"
  );
}

/** 判断文本是否为路由路径（以 / 开头且长度>1） */
export function isRoutePath(text: string): boolean {
  return /^\/[a-zA-Z0-9\-_]+$/.test(text);
}

/** AI 欢迎语文本特征（历史加载时过滤，避免每次进页面都重复显示登录欢迎语） */
const GREETING_TEXT_RE = /你好|您好|哈喽|嗨|老板好|我是小伊/;

/**
 * 是否应跳过（不显示）该条 AI 欢迎语
 *
 * ★ 统一规则：msg_type === "greeting" 精确匹配，或（无 msg_type/action 时）文本命中欢迎语特征。
 *   带 action（如 navigation）或带查询 msg_type 的消息不视为欢迎语。
 */
export function isGreetingMessage(m: any): boolean {
  if (!m || m.role === "user") return false;
  if (m.msg_type === "greeting") return true;
  if (m.msg_type || m.action) return false;
  return GREETING_TEXT_RE.test(String(m.text || ""));
}

/**
 * 把可能是 JSON 字符串的 data 归一化为对象
 *
 * ★ 后端多数情况返回 object，但少数场景（如历史接口）可能返回 JSON 字符串，
 *   直接当对象用会取不到字段，导致卡片空白。
 */
function normalizeData(raw: unknown): Record<string, any> | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "object") return raw as Record<string, any>;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s || (s[0] !== "{" && s[0] !== "[")) return undefined; // 纯文本，非 JSON
    try {
      const parsed = JSON.parse(s);
      return typeof parsed === "object" && parsed !== null ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * 规范化查询消息的 queryData
 *
 * - 订单消息（action=book）：{ __data, ...data }，供 OrderCard 读取 __data
 * - 其他查询消息：直接使用 data
 *
 * @returns 需要渲染卡片时返回数据对象，否则返回 undefined
 */
export function buildQueryData(d: any): any {
  if (!d) return undefined;
  const base = normalizeData(d.data);

  if (isBookingOrderMsg(d)) {
    // __data 优先（后端订单详情），同时展开 data 作为兜底字段
    const orderPayload = normalizeData(d.__data) || base;
    if (!orderPayload) return undefined;
    return base ? { __data: orderPayload, ...base } : { __data: orderPayload };
  }

  if (isQueryMsgType(d.msg_type)) {
    return base;
  }
  return undefined;
}
